'use client';

import { useMemo, type FC } from 'react';
import type {
  IBaseContentMedia,
  IBaseContentLink
} from '@/services/isomorphic/ContentService';
import { classes } from '@/next-utils/css-utils/scss-utils';
import type { Nullable } from '@/type-utils';
import { Link } from '@/react/components/core-ui/Link';
import { getMediaList } from '../Media';
import ContentMedia from '../../core/ContentMedia';

import S from './styles.base.module.scss';

interface INavigationTileProps {
  /**
   * Headline for the tile.
   */
  headline?: string;
  /**
   * Media items to display on different screen widths.
   */
  mediaItems?: Array<IBaseContentMedia>;
  /**
   * The link for this piece of content.
   */
  link?: Nullable<IBaseContentLink>;
  /**
   */
  textAlignmentY: 'top' | 'bottom';
  /**
   * Horizontal text alignment for headline.
   */
  textAlignmentX: 'left' | 'right' | 'center';
  /**
   * Optional callback for handling when links are clicked.
   */
  handleLinkClick?: () => void;
  /**
   * Optional className to override tile link css.
   */
  linkStyleOverride?: string;
}

/**
 * Single Navigation Tile Component.
 */
export const NavigationTile: FC<INavigationTileProps> = ({
  headline,
  mediaItems = [],
  link,
  textAlignmentY,
  textAlignmentX,
  handleLinkClick,
  linkStyleOverride
}) => {
  const mediaList = getMediaList(mediaItems);

  const defaultMedia =
    mediaList.mobile[0] ?? mediaList.tablet[0] ?? mediaList.desktop[0];

  const headlineComponent = useMemo(() => {
    return (
      <div
        className={classes(
          S.textPrimary,
          textAlignmentX === 'center' && S.center,
          textAlignmentX === 'left' && S.left,
          textAlignmentX === 'right' && S.right
        )}
      >
        {headline}
      </div>
    );
  }, [headline, textAlignmentX]);

  const tileComponent = useMemo(() => {
    return (
      <div className={S.tile}>
        {textAlignmentY === 'top' && headlineComponent}
        <div className={S.image}>
          <ContentMedia
            media={{
              defaultMedia,
              tabletMedia: mediaList.tablet[0] ?? defaultMedia,
              desktopMedia: mediaList.desktop[0] ?? defaultMedia
            }}
          />
        </div>
        {textAlignmentY === 'bottom' && headlineComponent}
      </div>
    );
  }, [
    defaultMedia,
    headlineComponent,
    mediaList.tablet,
    mediaList.desktop,
    textAlignmentY
  ]);

  return (
    <>
      {link ? (
        <Link
          onClick={handleLinkClick}
          className={(S.link, linkStyleOverride)}
          href={link.link}
          variant="text-underline"
        >
          {tileComponent}
        </Link>
      ) : (
        <>{tileComponent}</>
      )}
    </>
  );
};
